export type UndeliveredAuditEntry = {
  invocationId: string;
  source: 'ui' | 'webmcp';
  toolName: string | null;
  operation: string;
  outcome: string;
  completedAt: string;
  auditStatus: 'pending' | 'failed';
  workingRevision: number | null;
  errorCode: string | null;
};

export type UndeliveredAuditIngestPayload = {
  invocationId: string;
  templateId: string;
  source: 'ui' | 'webmcp';
  toolName: string | null;
  operation: string;
  startedAt: string;
  completedAt: string;
  durationMs: number;
  workingRevision: number | null;
  persistedRevision: number | null;
  outcome: string;
  errorCode: string | null;
  inputSummary: Record<string, unknown> | null;
  outputSummary: Record<string, unknown> | null;
};

type OutboxRecord = {
  templateId: string;
  entry: UndeliveredAuditEntry;
  ingestPayload: UndeliveredAuditIngestPayload;
  attemptCount: number;
};

const LEGACY_STORAGE_KEY = 'template-builder-audit-outbox';
const STORAGE_KEY_PREFIX = 'template-builder-audit-outbox:';
const MAX_DELIVERY_ATTEMPTS = 10;
const RETENTION_MS = 90 * 24 * 60 * 60 * 1000;

const AUDIT_OUTCOMES = new Set([
  'success',
  'validation_failure',
  'cancelled',
  'authorization_failure',
  'unexpected_failure',
]);

const outbox = new Map<string, OutboxRecord>();

let storage: Storage | null = typeof localStorage !== 'undefined' ? localStorage : null;
let storageWarned = false;
let outboxUserId: string | null = null;

function warnStorageOnce(error: unknown): void {
  if (storageWarned) {
    return;
  }
  storageWarned = true;
  console.warn('[Template operation audit] could not persist outbox to localStorage', error);
}

function storageKeyForUser(userId: string): string {
  return `${STORAGE_KEY_PREFIX}${userId}`;
}

function canUseOutboxStorage(): boolean {
  return Boolean(storage && outboxUserId && outboxUserId.length > 0);
}

function isValidIngestPayload(payload: unknown): payload is UndeliveredAuditIngestPayload {
  if (!payload || typeof payload !== 'object') {
    return false;
  }
  const row = payload as UndeliveredAuditIngestPayload;
  if (row.source !== 'ui' && row.source !== 'webmcp') {
    return false;
  }
  if (!AUDIT_OUTCOMES.has(row.outcome)) {
    return false;
  }
  if (typeof row.invocationId !== 'string' || row.invocationId.length === 0) {
    return false;
  }
  if (typeof row.operation !== 'string' || row.operation.length === 0) {
    return false;
  }
  if (typeof row.templateId !== 'string' || row.templateId.length === 0) {
    return false;
  }
  const startedMs = Date.parse(row.startedAt);
  const completedMs = Date.parse(row.completedAt);
  if (!Number.isFinite(startedMs) || !Number.isFinite(completedMs)) {
    return false;
  }
  if (typeof row.durationMs !== 'number' || !Number.isFinite(row.durationMs)) {
    return false;
  }
  return true;
}

function isRecordExpired(completedAt: string): boolean {
  const completedMs = Date.parse(completedAt);
  if (!Number.isFinite(completedMs)) {
    return true;
  }
  return Date.now() - completedMs > RETENTION_MS;
}

function isValidStoredRow(row: unknown): row is OutboxRecord {
  if (!row || typeof row !== 'object') {
    return false;
  }
  const record = row as OutboxRecord;
  if (!record.templateId || !record.entry || !record.ingestPayload) {
    return false;
  }
  if (!isValidIngestPayload(record.ingestPayload)) {
    return false;
  }
  const attemptCount =
    typeof record.attemptCount === 'number' && Number.isFinite(record.attemptCount)
      ? record.attemptCount
      : 0;
  if (attemptCount >= MAX_DELIVERY_ATTEMPTS) {
    return false;
  }
  if (isRecordExpired(record.ingestPayload.completedAt)) {
    return false;
  }
  return true;
}

function persistOutbox(): void {
  if (!canUseOutboxStorage()) {
    return;
  }
  try {
    const serialized: Record<string, OutboxRecord> = {};
    for (const [invocationId, row] of outbox) {
      serialized[invocationId] = row;
    }
    storage!.setItem(storageKeyForUser(outboxUserId!), JSON.stringify(serialized));
  } catch (error) {
    warnStorageOnce(error);
  }
}

export function configureAuditOutboxStorage(next: Storage | null): void {
  storage = next;
}

export function setAuditOutboxUserId(userId: string | null | undefined): void {
  const next = userId?.trim() ?? '';
  if (next === outboxUserId) {
    return;
  }
  outbox.clear();
  outboxUserId = next.length > 0 ? next : null;
  if (!canUseOutboxStorage()) {
    return;
  }
  try {
    storage!.removeItem(LEGACY_STORAGE_KEY);
  } catch {
    // ignore
  }
  hydrateAuditOutboxFromStorage();
}

export function hydrateAuditOutboxFromStorage(): void {
  if (!canUseOutboxStorage()) {
    return;
  }
  outbox.clear();
  try {
    const raw = storage!.getItem(storageKeyForUser(outboxUserId!));
    if (!raw) {
      return;
    }
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    let changed = false;
    for (const [invocationId, row] of Object.entries(parsed)) {
      if (isValidStoredRow(row)) {
        const record = row as OutboxRecord;
        outbox.set(invocationId, {
          ...record,
          attemptCount:
            typeof record.attemptCount === 'number' && Number.isFinite(record.attemptCount)
              ? record.attemptCount
              : 0,
        });
      } else {
        changed = true;
      }
    }
    if (changed || Object.keys(parsed).length !== outbox.size) {
      persistOutbox();
    }
  } catch (error) {
    warnStorageOnce(error);
    try {
      storage!.removeItem(storageKeyForUser(outboxUserId!));
    } catch {
      // ignore
    }
  }
}

export function resetAuditDeliveryForTests(): void {
  outbox.clear();
  storageWarned = false;
  if (storage) {
    try {
      storage.removeItem(LEGACY_STORAGE_KEY);
      if (outboxUserId) {
        storage.removeItem(storageKeyForUser(outboxUserId));
      }
    } catch {
      // ignore
    }
  }
}

/** Clears the in-memory map only (simulates a full page reload with storage intact). */
export function clearAuditOutboxMemoryForTests(): void {
  outbox.clear();
}

export function rememberUndeliveredAudit(
  templateId: string,
  entry: UndeliveredAuditEntry,
  ingestPayload: UndeliveredAuditIngestPayload,
  attemptCount?: number,
): void {
  const previous = outbox.get(entry.invocationId);
  const count = attemptCount ?? previous?.attemptCount ?? 0;
  outbox.set(entry.invocationId, {
    templateId,
    entry,
    ingestPayload,
    attemptCount: count,
  });
  if (canUseOutboxStorage()) {
    persistOutbox();
  }
}

/** Returns null when the row is evicted after reaching the attempt limit. */
export function incrementUndeliveredAuditAttempts(invocationId: string): number | null {
  const row = outbox.get(invocationId);
  if (!row) {
    return null;
  }
  const nextCount = row.attemptCount + 1;
  if (nextCount >= MAX_DELIVERY_ATTEMPTS) {
    console.error('[Template operation audit] dropping undelivered audit after max attempts', {
      invocationId,
      templateId: row.templateId,
      operation: row.ingestPayload.operation,
      attemptCount: nextCount,
    });
    forgetUndeliveredAudit(invocationId);
    return null;
  }
  if (canUseOutboxStorage()) {
    persistOutbox();
  }
  return nextCount;
}

export function forgetUndeliveredAudit(invocationId: string): void {
  outbox.delete(invocationId);
  if (canUseOutboxStorage()) {
    persistOutbox();
  }
}

export function readUndeliveredAudit(
  invocationId: string,
): { templateId: string; ingestPayload: UndeliveredAuditIngestPayload } | null {
  const row = outbox.get(invocationId);
  if (!row) {
    return null;
  }
  return { templateId: row.templateId, ingestPayload: row.ingestPayload };
}

export function listUndeliveredAudits(templateId: string): UndeliveredAuditEntry[] {
  return [...outbox.values()]
    .filter((row) => row.templateId === templateId)
    .map((row) => row.entry);
}

export function listUndeliveredAuditInvocationIds(templateId: string): string[] {
  return [...outbox.entries()]
    .filter(([, row]) => row.templateId === templateId)
    .map(([invocationId]) => invocationId);
}

export function mergeUndeliveredActivity<T extends { invocationId: string }>(
  templateId: string,
  page: { entries: T[]; nextCursor: string | null },
  cursor?: string,
): { entries: Array<T | UndeliveredAuditEntry>; nextCursor: string | null } {
  if (cursor) {
    return page;
  }
  const delivered = new Set(page.entries.map((entry) => entry.invocationId));
  const extra = listUndeliveredAudits(templateId).filter(
    (entry) => !delivered.has(entry.invocationId),
  );
  if (extra.length === 0) {
    return page;
  }
  return {
    ...page,
    entries: [...extra, ...page.entries],
  };
}

/** Test helper: read persisted JSON for a user without switching the active outbox user. */
export function readPersistedOutboxForUser(userId: string): Record<string, OutboxRecord> | null {
  if (!storage) {
    return null;
  }
  const raw = storage.getItem(storageKeyForUser(userId));
  if (!raw) {
    return null;
  }
  return JSON.parse(raw) as Record<string, OutboxRecord>;
}
